import { RADIUS } from "@elyxeer/ui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { useLocale } from "~/i18n";
import type { EventQuestion } from "~/types/event";

const MAX_LENGTH = 1000;

/**
 * Questions for the moderator: a composer and the asker's own list with its status.
 *
 * Shared by the member detail and the public registration page, so it knows nothing
 * about how a question is sent — the caller hands it `onAsk` and the list it already
 * holds. Questions go to the moderator, never to a chat: the private conversation is
 * the wrong room for something meant to be read aloud, and the moderator wants one list
 * per broadcast rather than one per member.
 */
export function EventQuestions({
  questions,
  open,
  onAsk,
  testIdPrefix = "event",
}: {
  questions: EventQuestion[];
  /** Whether the moderator is still taking questions. */
  open: boolean;
  onAsk: (body: string) => Promise<unknown>;
  testIdPrefix?: string;
}) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const submit = async (submitEvent: FormEvent) => {
    submitEvent.preventDefault();
    const body = draft.trim();
    if (body.length < 2 || sending) return;
    setSending(true);
    setErrorKey(null);
    setSent(false);
    try {
      await onAsk(body);
      setDraft("");
      setSent(true);
    } catch {
      setErrorKey("events.questions.error");
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      component="section"
      data-testid={`${testIdPrefix}.questions`}
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Typography variant="H2" sx={{ color: "text.primary" }}>
        {t("events.questions.title")}
      </Typography>

      {open ? (
        <Box
          component="form"
          onSubmit={(submitEvent) => void submit(submitEvent)}
          sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
        >
          <TextField
            value={draft}
            onChange={(changeEvent) => setDraft(changeEvent.target.value)}
            placeholder={t("events.questions.placeholder")}
            multiline
            minRows={2}
            fullWidth
            slotProps={{
              htmlInput: {
                maxLength: MAX_LENGTH,
                "data-testid": `${testIdPrefix}.question-input`,
                "aria-label": t("events.questions.ask"),
              },
            }}
          />
          <Typography variant="T3" sx={{ color: "text.muted" }}>
            {t("events.questions.hint")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              type="submit"
              variant="outlined"
              disabled={sending || draft.trim().length < 2}
              data-testid={`${testIdPrefix}.question-send`}
            >
              {t("events.questions.send")}
            </Button>
            {sent ? (
              <Typography
                variant="T3"
                sx={{ color: "success.main" }}
                data-testid={`${testIdPrefix}.question-sent`}
              >
                {t("events.questions.sent")}
              </Typography>
            ) : null}
          </Box>
          {errorKey ? <Alert severity="error">{t(errorKey)}</Alert> : null}
        </Box>
      ) : (
        <Typography
          variant="T2"
          sx={{ color: "text.secondary" }}
          data-testid={`${testIdPrefix}.questions-closed`}
        >
          {t("events.questions.closed")}
        </Typography>
      )}

      {questions.length === 0 ? (
        open ? null : (
          <Typography variant="T3" sx={{ color: "text.muted" }}>
            {t("events.questions.empty")}
          </Typography>
        )
      ) : (
        <Box
          component="ul"
          data-testid={`${testIdPrefix}.question-list`}
          sx={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {questions.map((question) => (
            <Box
              component="li"
              key={question.id}
              data-testid={`${testIdPrefix}.question`}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: `${RADIUS.md}px`,
                padding: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography variant="T2" sx={{ color: "text.primary" }}>
                {question.body}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  color={question.answered_at ? "success" : "default"}
                  label={t(
                    question.answered_at
                      ? "events.questions.answered"
                      : "events.questions.pending",
                  )}
                />
                <Typography variant="T3" sx={{ color: "text.muted" }}>
                  <time dateTime={question.created_at}>
                    {formatDate(question.created_at, {
                      day: "numeric",
                      month: "long",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </time>
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
